import Vue from 'vue'
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import Sortable from 'sortablejs'
import cheerio from 'cheerio'
import moment from 'moment'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import businessDays from 'business-days-js'
import _ from 'lodash'
import lodash from 'lodash'
import hash from 'object-hash'
import mime from 'mime-types'
import {camelCase, capitalCase} from 'change-case'
import {OK} from 'http-status-codes'
import {ObjectID} from 'bson'

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)

export function parseActiveDirectoryUserId(auth0UserId) {
  if (!auth0UserId) {
    console.error('Unable to parse auth0UserId')
    return null
  }
  return auth0UserId.split('|')[2]
}

export function formatTimezoneOffset() {
  const offset = new Date().getTimezoneOffset();
  const sign = offset <= 0 ? "+" : "-"; // Positive for UTC+, negative for UTC-
  const absoluteOffset = Math.abs(offset); // Make sure to work with positive value
  const hours = String(Math.floor(absoluteOffset / 60)).padStart(2, "0");
  const minutes = String(absoluteOffset % 60).padStart(2, "0");

  return `${sign}${hours}:${minutes}`;
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function camelize(str) {
  if (!str || typeof str !== 'string') {
    return str
  }

  str = str.trim()

  // if string starts with digits, move to end of the string
  const found = str.match(/^\d+/g)

  if (found) {
    str = str.substr(found[0].length + 1, str.length) + found[0]
  }

  str = str.replace(/\./g, '')
  str = str.replace(/'/g, '')

  const almostCamelCase = camelCase(str)
  return almostCamelCase.replaceAll('_', '')
}

export function cleanCamundaDeploymentName(fileName) {
  return fileName.replace(/\.bpmn/, '').replace(/_/g, ' ')
}

export function ucFirst(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function sortBySort(unsortedArray) {
  return sortBy(unsortedArray, 'sort')
}

export function sortByName(unsortedArray) {
  return sortBy(unsortedArray, 'name')
}

export function sortBy(unsortedArray, prop) {
  if (!Array.isArray(unsortedArray)) {
    console.error('sortBy - unsortedArray must be an array', unsortedArray)
    return unsortedArray
  }
  return [...unsortedArray].sort((a, b) => {
    let aa = _.get(a, prop)
    let bb = _.get(b, prop)

    // otherwise puts all words starting with a capital letter at the beginning
    if (typeof aa.toLowerCase === 'function' && typeof bb.toLowerCase === 'function') {
      aa = aa.toLowerCase()
      bb = bb.toLowerCase()
    }

    if (aa > bb) {
      return 1
    }
    if (aa < bb) {
      return -1
    }
    return 0
  })
}

export function sortByReverse(unsortedArray, prop) {
  return [...unsortedArray].sort((a, b) => {
    if (_.get(a, prop) > _.get(b, prop)) {
      return -1
    }
    if (_.get(a, prop) < _.get(b, prop)) {
      return 1
    }
    return 0
  })
}

export function sortByIgnoreCase(unsortedArray, prop) {
  return unsortedArray.sort((a, b) => a[prop].localeCompare(b[prop], undefined, {sensitivity: 'base'}))
}

export function taskNameKey(str) {
  // If name starts with a number, we need to move the numeric portion to the end of the key.
  // Translates to XML something or other in Camunda that can't start with a number.
  if (!isNaN(str.charAt(0))) {
    let matches = str.match(/^\d+/)
    if (matches) {
      const number = matches[0]
      str = str.substr(number.length + 1, str.length)
      str += '_' + number
    }
  }

  return str.replace(/ /g, '_')
}

export function taskNameWithOptionalRepeatCountSuffix(task) {
  let name = task.name
  if (_.get(task, 'fieldValues.reinspectionCount') && task.fieldValues['reinspectionCount'] > 0) {
    name += ' (' + task.fieldValues['reinspectionCount'] + ')'
  } else if (_.get(task, 'fieldValues.repeatCount') && task.fieldValues['repeatCount'] > 0) {
    name += ' (' + task.fieldValues['repeatCount'] + ')'
  }
  return name
}

export function formatApiErrors(errors) {
  let errorMsg = '<div class="notify-errors"><h6>Errors</h6><ul>'
  if (errors) {
    errors.forEach((error) => {
      errorMsg += '<li>' + error.message + '</li>'
    })
  }
  errorMsg += '</ul></div>'
  return errorMsg
}

export function isHTML(str) {
  let doc = new DOMParser().parseFromString(str, 'text/html')
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1)
}

export function stripHTML(html) {
  if (!html) {
    return ''
  }

  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ""
}

export function notifyFileUploads(vue, fileUploadResponses) {
  for (const fileUploadResponse of fileUploadResponses) {
    if (fileUploadResponse.status === Constants.NOTIFY.SUCCESS) {
      notifySuccess(vue, fileUploadResponse.message)
    } else if (fileUploadResponse.status === Constants.NOTIFY.WARNING) {
      notifyWarning(vue, fileUploadResponse.message)
    } else if (fileUploadResponse.status === Constants.NOTIFY.ERROR) {
      notifyError(vue, fileUploadResponse.message)
    }
  }
}

export function notifyError(vue, msg, errors) {
  if (errors) {
    let errMsg = '<ul>'
    errors.forEach((error) => {
      errMsg += '<li>' + error.message + '</li>'
    })
    errMsg += '</ul>'

    if (msg) {
      msg = '<div><p>' + msg + '</p><p>' + errMsg + '</p></div>'
    } else {
      msg = errMsg
    }
  }

  const duration = 35
  const type = Constants.NOTIFY.DANGER

  let notify = {
    duration,
    type,
    message: msg
  }

  if (isHTML(msg)) {
    let customMessage = Vue.component('custom-message', {
      template: msg
    })
    notify = {
      duration,
      type,
      component: customMessage,
      icon: 'nc-icon nc-alert-circle-i',
    }
  }

  vue.$notify(notify)
}

export function notifySuccess(vue, msg) {
  vue.$notify({
    message: msg,
    icon: 'nc-icon nc-check-2',
    type: Constants.NOTIFY.SUCCESS
  })
}

export function notifyWarning(vue, msg) {
  vue.$notify({
    message: msg,
    icon: 'nc-icon nc-alert-circle-i',
    type: Constants.NOTIFY.WARNING
  })
}

export function fieldName(field) {
  return field.label.toLowerCase()
}

export function generateDynamicFieldValidation(component) {
  component.fieldName = fieldName(component.field)

  if (component.field.required) {
    component.fieldValidation.required = true
  }

  if (component.field.min > 0) {
    component.fieldValidation.min = component.field.min
  }

  if (component.field.max > 0) {
    component.fieldValidation.max = component.field.max
  }

  if (component.field.pattern) {
    component.fieldValidation.regex = component.field.pattern
  }
}

export function axiosError(errors, error) {
  let newErrors = _.cloneDeep(errors)
  if (_.get(error, 'response.data.errors')) {
    for (let err of error.response.data.errors) {
      newErrors.push(err)
    }
  } else if (_.get(error, 'response.data.cause.message')) {
    newErrors.push({
      message: _.get(error, 'response.data.cause.message')
    })
  } else if (error.request) {
    // The request was made but no response was received
    newErrors.push({
      message: 'Failed to receive response from remote resource'
    })
  } else {
    // Something unexpected happened in setting up the request that triggered an Error
    newErrors.push({
      message: 'An unexpected error occurred: ' + error.message
    })
  }

  return newErrors
}

export function axiosErrorMessage(axiosError) { // Can exapand as needed to handle multiple properties or errors
  const errors = axiosError.errors
  if (errors) {
    return errors[0].message.replace('{0}', errors[0].property)
  }
}

export function objectBy(objList, by, val) {
  const search = {}
  search[by] = val
  return _.find(objList, search)
}

export function objectById(objList, objId) {
  return _.find(objList, {id: objId})
}

export function objectByName(objList, objName) {
  return _.find(objList, {name: objName})
}

export function objectNameById(objList, objId) {
  const obj = _.find(objList, {id: objId})
  let objName = null
  if (obj !== undefined) {
    objName = obj.name
  }
  return objName
}

export function objectNamesById(objList, objIds) {
  const kvps = []
  if (objIds) {
    objIds.forEach((objId) => {
      let name = objectNameById(objList, objId)
      if (name) {
        kvps.push(
          name
        )
      }
    })
  }
  return kvps
}

export function deleteBy(objList, field, fieldValue) {
  return _.filter(objList, function (currentObject) {
    return currentObject[field] !== fieldValue
  })
}

export function contactsForSelect(companyContacts, contactId) {
  const kvps = []
  if (companyContacts) {
    companyContacts.forEach((contact) => {
      let name = contact.firstName
      if (contact.lastName) {
        name += ' ' + contact.lastName
      }
      if (!contact.active) {
        // We take in contactId in case the saved contact is Inactive. Otherwise, don't include Inactive contacts in select.
        if (contactId && contactId === contact.id) {
          name += ' - Inactive'
        } else {
          return
        }
      }
      kvps.push(
        {
          label: name,
          value: contact.id,
          contact: contact
        }
      )
    })
  }
  return sortBy(kvps, 'label')
}

export function kvpsForSelectFromArray(data) {
  // populate the counties select based on what city was selected
  const kvps = []
  if (data) {
    data.forEach(function (thing) {
      kvps.push(
        {
          label: thing,
          value: thing
        }
      )
    })
  }
  return sortBy(kvps, 'label')
}

export function uniqueKvpsForSelectFromArrayWithCsvValues(data, prop) {
  const kvps = []
  if (!Array.isArray(data)) {
    return kvps
  }
  data.forEach((datum) => {
    if (datum[prop]) {
      let values = [datum[prop]]

      // skip empty arrays
      if (values[0].length === 0) {
        return
      }

      if (datum[prop].indexOf(', ') > 0) {
        values = datum[prop].split(', ')
      }

      values.forEach((value) => {
        if (!_.find(kvps, {label: value})) {
          kvps.push({
            label: value,
            value: value
          })
        }
      })
    }
  })

  return sortBy(kvps, 'label')
}

export function uniqueKvpsForSelectFromArray(data, prop) {
  const uniqueValues = _.uniq(_.map(data, prop))
  const kvps = []
  uniqueValues.forEach((value) => {
    if (value && value.length > 0) {
      kvps.push({
        label: value,
        value: value
      })
    }
  })

  return sortBy(kvps, 'label')
}

export function defaultEmptyUserPreferences(allUserPreferences) {
  let preferenceKeys = _.map(allUserPreferences, 'key')
  const preferencesWithEmptyValues = {}
  preferenceKeys.forEach((preferenceKey) => {
    preferencesWithEmptyValues[preferenceKey] = null
  })
  return preferencesWithEmptyValues
}

export function getUserFullName(user) {
  if (user) {
    if (user.name) {
      return user.name
    } else if (user.username) {
      return user.username
    }
  }
  return ''
}

export function focusTrap(modalNumber, component) {
  const exceptions = [
    'no-city-results-found',
    'no-street-results-found',
    'street-results',
    'no-community-results-found',
    'no-company-results-found',
    'no-plan-name-results-found',
    'no-legal-subdivision-results-found'
  ]
  if (!exceptions.includes(component)) {
    document.getElementById(modalNumber).focus()
    document.getElementById(modalNumber).blur()
  }
}

export function formatIsoDate(value) {
  if (value) {
    if (typeof value.toISOString === 'function') {
      return value.toISOString().split('T')[0]
    }
    return value.split('T')[0]
  }
  return null
}

export function formatLatLng(str) {
  if (str) {
    const parts = str.split(' ')
    return {
      lat: Number(parts[0].trim()),
      lng: Number(parts[1].trim())
    }
  }
}

export function parseDriver(inTouchDeviceTitle) {
  return inTouchDeviceTitle.substring(inTouchDeviceTitle.indexOf('for ') + 4, inTouchDeviceTitle.indexOf(' on'))
}

export function cleanRssDescription(description) {
  return description.replace(', ,', ',')
}

export function deviceId(vin) {
  return '_' + vin
}

export function maybeGenerateProjectName (vue, projectPath) {
  const project = vue.$store.get(projectPath)
  const errors = []

  if (_.isEmpty(project.projectType)) {
    errors.push({ message: 'Select a project type'})
  }
  if (_.isEmpty(project.address.city)) {
    errors.push({ message: 'Select a city'})
  }
  if (_.isEmpty(project.address.state)) {
    errors.push({ message: 'Select a state'})
  }
  if (errors.length > 0) {
    notifyError(vue, 'To generate a project name please', errors)
    return
  }

  // Street Number, Street Name, Building, Floor, Suite, Lot, Block, City, State.
  const projectName = project.projectType + ' Project at ' +
      (project.address.streetNumber && project.address.streetName ? project.address.streetNumber + ' ' : '') +
      (project.address.streetName ? project.address.streetName + ', ' : '') +
      (project.address.buildingNumber ? 'Building ' + project.address.buildingNumber + ', ' : '') +
      (project.address.floorNumber ? 'Floor ' + project.address.floorNumber + ', ' : '') +
      (project.address.suiteNumber ? 'Suite ' + project.address.suiteNumber + ', ' : '') +
      (project.address.lotNumber ? 'Lot ' + project.address.lotNumber + ', ' : '') +
      (project.address.blockNumber ? 'Block ' + project.address.blockNumber + ', ' : '') +
      project.address.city + ', ' + project.address.state

  vue.$store.set(`${projectPath}@name`, projectName)
  vue.validateNameIsUnique()
}

export function weeksAgo(numWeeks) {
  return moment().subtract(numWeeks, 'w').format('MM-DD-YYYY')
}

export function notificationSeenNotSeen(notification) {
  if (notification.seenAt) {
    return 'notification-seen'
  }
  return 'notification-not-seen'
}

export function parseUsernameFromEmail(email) {
  if (email) {
    return email.substr(0, email.indexOf('@'))
  }
  return email
}

export function extractIncrementerFromMongoId(mongoId) {
  if (mongoId) {
    return mongoId.substr(18, 6)
  }
  return null
}

export function nameToDirName(name) {
  if (name) {
    name = name.replace(/\.|,/g, '') //remove punctuation
    return name.replace(/\//g, '^') // replace forward slash with caret
  }
  return null
}

export function nameToComparableName(name) {
  if (name) {
    return name.replace(/\.|,|\s|:/g, '').toLowerCase() //remove punctuation
  }
  return null
}

export function urlEncode(name) {
  if (name) {
    return name.replace(/\s/g, '%20') //remove punctuation
  }
  return null
}

export function fileSystemPath(projectName, projectId, departmentName, jobName, jobId) {
  let path = Constants.FOLDERS.PROJECTS
  path += '/' + nameToDirName(projectName) + ' ' + extractIncrementerFromMongoId(projectId)
  if (departmentName) {
    path += '/' + departmentName
    if (jobName && jobId) {
      path += '/' + jobName + ' ' + extractIncrementerFromMongoId(jobId)
    }
  }
  return path
}

export function fileSystemPathForTask(projectName, projectId, departmentName, jobName, jobId, taskName, taskId, repeatCount = 0) {
  let path = fileSystemPath(projectName, projectId, departmentName, jobName, jobId)
  path += '/' + nameToDirName(taskName) + ' ' + extractIncrementerFromMongoId(taskId)
  if (repeatCount > 0) {
    path += ' (' + repeatCount + ')'
  }
  return path
}

export function getRepeatCount(task) {
  let repeatCount = 0
  if (task.fieldValues) {
    if (task.fieldValues['reinspectionCount']) {
      repeatCount = task.fieldValues['reinspectionCount']
    } else if (task.fieldValues['repeatCount']) {
      repeatCount = task.fieldValues['repeatCount']
    }
  }
  return repeatCount
}

export function getEntityFolderName(entity) {
  return nameToDirName(entity.name) + ' ' + extractIncrementerFromMongoId(entity.id)
}

export function autocompleteReady(text, numChars = Constants.AUTOCOMPLETE.TRIGGER_LENGTH) {
  return text && text.length >= numChars
}

export function searchFieldsHaveValues(query, searchFields) {
  return searchFields.some(
    searchField =>
      (_.isString(query[searchField]) && !_.isEmpty(query[searchField])) ||
      (_.isArray(query[searchField]) && query[searchField].length > 0) ||
      (_.isBoolean(query[searchField]) && query[searchField] === true)
  )
}

export function kvpsForSelectBasic(entities, sortByProp, labelProp, valueProp) {

  const sortedEntities = sortBy(entities, sortByProp)

  const kvps = []
  sortedEntities.forEach((entity) => {
    const kvp = {
      label: entity[labelProp],
      value: entity[valueProp]
    }
    kvps.push(kvp)
  })

  return kvps
}

export function kvpsForSelectTheWorks(entities, opts) {
  let labelProp = 'id'
  let valueProp = 'name'
  let includeInactive = false
  let allEntities = entities
  let extraValue = null

  if (opts) {
    if (opts.hasOwnProperty('sortBy')) {
      allEntities = sortBy(entities, opts.sortBy)
    }

    if (opts.hasOwnProperty('labelProp')) {
      labelProp = opts.labelProp
    }

    if (opts.hasOwnProperty('valueProp')) {
      valueProp = opts.valueProp
    }

    if (opts.hasOwnProperty('extraValue')) {
      extraValue = opts.extraValue
    }

    if (opts.hasOwnProperty('includeInactive')) {
      includeInactive = opts.includeInactive
    }
  }

  const activeKvps = []
  const inactiveKvps = []

  allEntities.forEach((entity) => {
    const kvp = {
      label: _.get(entity, labelProp),
      value: _.get(entity, valueProp)
    }

    if (extraValue && entity.hasOwnProperty(extraValue)) {
      kvp.label += ' (' + entity[extraValue] + ')'
    }

    const isInactive = entity.active === false

    if (isInactive && includeInactive) {
      inactiveKvps.push(kvp)
    } else if (!isInactive) {
      activeKvps.push(kvp)
    }
  })

  if (inactiveKvps.length > 0 && activeKvps.length > 0) {
    const divider = {
      label: '──────────────',
      value: 'divider',
      disabled: true
    }

    return [...activeKvps, divider, ...inactiveKvps]
  }

  return [...activeKvps, ...inactiveKvps]
}

export function dateNoon(date) {
   const dayJS = dayjs(date)
  // Setting to 12pm so queries that don't account for the difference in the browser's timezone and the server in UTC
  // will still work respond as expected. IE, the date won't roll over to the next day UTC
  if (dayJS.isValid()) {
    return dayJS.set('hour', 12).set('minute', 0).set('second', 0).format()
  }

  console.error('dateNoon failed to parse date and set the value to 12pm', date)

  return date
}

export function dateReceived() {
  // Setting to 12pm so queries that don't account for the difference in the browser's timezone and the server in UTC
  // will still work respond as expected. IE, the date won't roll over to the next day UTC
  return dayjs().set('hour', 12).set('minute', 0).set('second', 0).format()
}

export function nextBusinessDay() {
  // set to 12pm next day
  const newDate = businessDays({state: 'tx', excludeHolidays: Constants.ATS.EXCLUDE_HOLIDAYS}).addDays(new Date(), 1, { excludeInitialDate: true }) // Initializing date as dayjs() breaks businessDays(). MUST be new Date()
  return dayjs(newDate).set('hour', 12).set('minute', 0).set('second', 0).format()
}

export function utcToLocal(value) {
  const date = dayjs(value)

  if (!date.isValid()) {
    return null
  }

  return date.toDate()
}

export function utcToLocalForPatch(value) {
  const date = dayjs(value)

  if (!date.isValid()) {
    return null
  }

  return date.format('YYYY-MM-DD') // backend patch can only deserialize string dates
}

export function flexmonsterIndexName(name) {
  const date = dayjs(new Date())
  const formatted = date.format('YYYY-MM-DD--HH-mm-ss')
  return name.toLowerCase().replaceAll(' ', '-') + '--' + formatted
}

export function formatDate(value) {
  if (value) {
    const date = dayjs(value)
    if (date.isValid()) {
      return date.format('MMMM DD, YYYY')
    }
  }
  return null
}

export function formatDateForQuickBooks(value) {
  if (value) {
    const date = dayjs(value)
    if (date.isValid()) {
      return date.format('YYYY-MM-DD')
    }
  }
  return null
}

export function formatDateTime(value) {
  if (value) {
    const date = dayjs(value)
    if (date.isValid()) {
      return date.format('MMMM D, YYYY - h:mm a')
    }
  }
  return null
}

export function encodeDateTimeToURI(value) {
  return encodeURI(value).replaceAll('+', '%2B')
}

export function formatCommentDate(value) {
  if (value) {
    const date = dayjs(value)
    if (date.isValid()) {
      return date.fromNow()
    }
  }
  return null
}

export function warnIfDueDateIsDayOff(vue, taskOrPayload) {
  if (taskOrPayload.dueDate && taskOrPayload.assigneeId) {
    const user = vue.$store.get('users/byId', taskOrPayload.assigneeId)

    if (!_.isEmpty(_.get(user, 'daysOff'))) {
      const formattedDaysOff = user.daysOff.map(date => formatDateForQuickBooks(date))

      if (_.includes(formattedDaysOff, formatDateForQuickBooks(taskOrPayload.dueDate))) {
        notifyWarning(vue, `${user.name} has a day off on ${formatDate(taskOrPayload.dueDate)}.
        ${taskOrPayload.taskName ? ` (Task Name: ${taskOrPayload.taskName})` : ''}`)
      }
    }
  }
}

export function setLocalStorage(location, data) {
  localStorage.setItem(location, JSON.stringify(data))
  localStorage.setItem(location + Constants.LOCAL_STORAGE.SET_TIME, JSON.stringify(new Date()))
}

export function checkLocalStorage(state, locationPath, expirationPeriod) {
  let location = locationPath
  if (_.isObject(locationPath)) {
    location = locationPath.enityType + locationPath.module
  }

  if (!localStorage.getItem(location)) {
    return false
  }

  const localStorageValue = JSON.parse(localStorage.getItem(location))

  if (!_.isEmpty(localStorageValue)) {
    if (expirationPeriod && Math.abs((new Date().getTime() - new Date(JSON.parse(localStorage.getItem(location + Constants.LOCAL_STORAGE.SET_TIME))).getTime()) / Constants.TIME.ONE_HOUR) < expirationPeriod) {
      // If localStorage data is < 1 day/month old and localState is empty, set localState from localStorage
      if (locationPath.module === Constants.LOCAL_STORAGE.LOCATION.STATUS_DATA && state[locationPath.enityType].length === 0) {
        state[locationPath.enityType] = localStorageValue
      } else if (locationPath.module === Constants.LOCAL_STORAGE.LOCATION.PRIORITIES_DATA && state[locationPath.enityType].length === 0) {
        state[locationPath.enityType] = localStorageValue
      } else if (location === Constants.LOCAL_STORAGE.LOCATION.USER_MAP_DATA && _.isEmpty(state.resultsMap)) {
        state.resultsMap = localStorageValue
      } else if (location === Constants.LOCAL_STORAGE.LOCATION.ALL_USER_PREFERENCES && !state.allUserPreferencesLoaded) {
        state.allUserPreferences = localStorageValue
        state.allUserPreferencesLoaded = true
      } else if (state.results && state.results.length === 0) {
        state.results = localStorageValue
      }
      return true
    }

    // If localStorage data is more than a day/month old, reset it
    clearLocalStorage(location)
    if (location === Constants.LOCAL_STORAGE.LOCATION.ALL_USER_PREFERENCES) {
      state.allUserPreferencesLoaded = false
    }
  }
}

export function clearLocalStorage(location) {
  if (localStorage.getItem(location)) {
    localStorage.removeItem(location)
    localStorage.removeItem(location + Constants.LOCAL_STORAGE.SET_TIME)
  }
}

export function formatCurrency(value) {
  if (!isNaN(value) && value >= 0) {
    return Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  if (value === Constants.DO_NOT_DISPLAY_PRICE_VALUE) {
    return ''
  }

  return ''
}

export function formatFileSize(bytes) {
  let exp = Math.log(bytes) / Math.log(1024) | 0
  let result = (bytes / Math.pow(1024, exp)).toFixed(2)
  return result + ' ' + (exp === 0 ? 'bytes' : 'KMGTPEZY'[exp - 1] + 'B')
}

export function formatToCsv(data) {
  if (data) {
    return data.join(', ')
  }
  return null
}

export function formatPrintBool(value) {
  if (value) {
    return 'Yes'
  }
  return 'No'
}

export function priorityLabel(numericPriority) {
  switch (numericPriority) {
    case 20:
    case '20':
      return Constants.PRIORITIES.VERY_LOW.LABEL
    case 40:
    case '40':
      return Constants.PRIORITIES.LOW.LABEL
    case 60:
    case '60':
      return Constants.PRIORITIES.MEDIUM.LABEL
    case 80:
    case '80':
      return Constants.PRIORITIES.HIGH.LABEL
    case 100:
    case '100':
      return Constants.PRIORITIES.VERY_HIGH.LABEL
  }
  return numericPriority
}

export function printModal(containerId) {
  setTimeout(() => {
    const results = document.getElementById(containerId)
    const cloned = results.cloneNode(true)
    let section = document.getElementById('print')

    if (!section) {
      section = document.createElement('div')
      section.id = 'print'
      document.body.appendChild(section)
    }

    section.innerHTML = ''
    section.appendChild(cloned)
    window.print()
  }, 500)
}

export function icon(file) {
  if (!file) {
    return 'unknown.png'
  }

  const ext = file.substr(file.lastIndexOf('.') + 1, file.length)

  let icon

  switch (ext) {
    case 'pdf':
      icon = 'pdf.png'
      break;
    case 'doc':
    case 'docx':
      icon = 'word.png'
      break;
    case 'xls':
    case 'xlsx':
      icon = 'excel.png'
      break;
    default:
      icon = 'unknown.png'
  }

  return icon
}

export function calculateUploadProgressInteger(progressEvent) {
  return Math.round((progressEvent.loaded * 100) / progressEvent.total)
}

export function qbLineItems(tasks) {
  if (tasks && !Array.isArray(tasks)) {
    console.error('qbLineItems - tasks variable is not an array', tasks)
    return
  }

  const lineItems = []

  tasks.forEach((invoiceTask) => {
    if (invoiceTask.job.invoiceBy === Constants.INVOICE_BY.MULTIPLE_TASKS && invoiceTask.linkedTasks) {
      for (const invoiceableTask of invoiceTask.linkedTasks) {
        addLineItemsToInvoiceTask(lineItems, invoiceableTask, invoiceTask.id)
      }
    } else { // default case
      addLineItemsToInvoiceTask(lineItems, invoiceTask)
    }
  })

  return lineItems
}

function addLineItemsToInvoiceTask(lineItems, task, invoiceTaskId = null) {
  lineItems.push({
    itemRef: task.fieldValues.qbItemCode,
    description: task.fieldValues.qbItemDescription,
    quantity: 1,
    classRef: task.fieldValues.qbClass,
    amount: maybeAppendCents(task.fieldValues.price),
    jobId: task.jobId,
    projectId: task.projectId,
    invoiceSubmittalTaskId: invoiceTaskId ? invoiceTaskId : task.id
  })

  if (task.fieldValues.travelCharge) {
    lineItems.push({
      itemRef: Constants.QUICKBOOKS.TRAVEL_CHARGE.ITEM_CODE,
      description: Constants.QUICKBOOKS.TRAVEL_CHARGE.DESCRIPTION,
      quantity: 1,
      classRef: task.fieldValues.qbClass,
      amount: maybeAppendCents(task.fieldValues.travelCharge),
      jobId: task.jobId,
      projectId: task.projectId,
      invoiceSubmittalTaskId: invoiceTaskId ? invoiceTaskId : task.id
    })
  }
}

export function assembleTravelChargeUrl(job) {
  if (!job.name) {
    console.error('job.name is empty. Unable to calculate travel charge.')
    return null
  }
  if (!job.clientId) {
    console.error('job.clientId is empty. Unable to calculate travel charge.')
    return null
  }
  if (!job.departmentId) {
    console.error('job.departmentId is empty. Unable to calculate travel charge.')
    return null
  }
  if (!job.projectId) {
    console.error('job.projectId is empty. Unable to calculate travel charge.')
    return null
  }
  if (!job.processDefinitionId) {
    console.error('job.processDefinitionId is empty. Unable to calculate travel charge.')
    return null
  }
  let url = 'travel-charge?' +
    'jobName=' + urlEncode(job.name) +
    '&clientId=' + job.clientId +
    '&departmentId=' + job.departmentId +
    '&projectId=' + job.projectId +
    '&processDefinitionId=' + job.processDefinitionId

  if (job.invoiceBy) {
    url += '&invoiceBy=' + job.invoiceBy
  }

  return url
}

export function maybeAppendCents(price) {
  if (!price) {
    return '0'
  }

  price = price.toString()

  // 100 -> 100.00
  if (!price.includes('.')) {
    price += '.00'
  }
  // 162.5 -> 162.50
  if (price.match(/^\d+\.\d$/)) {
    price += '0'
  }

  return price
}

export function canEditTaskField(component) {
  if (component.$can('edit', Permissions.COMPLETED_TASKS)) {
    return true
  }

  return component.statusOnLoad !== Constants.STATUSES.CANCELLED
    && component.statusOnLoad !== Constants.STATUSES.COMPLETED
}

export async function findCommunities(self, text, cb, statePath) {
  self.$store.set(statePath, null)

  if (autocompleteReady(text)) {
    const response = await self.$store.dispatch('communities/fetchByNameStartsWith', text)

    if (OK === response.status) {
      const communities = response.data
      if (communities && communities.length > 0) {
        cb(communities)
        return
      }

      if (self.$refs.community) {
        self.$refs.community.$refs.suggestions.showPopper = false // closes autocomplete results
      } else if (self.$refs.communityId) {
        self.$refs.communityId.$refs.suggestions.showPopper = false // closes autocomplete results
      }

      const community = {
        name: text,
        active: true
      }

      if (self.selectedClient) {
        community.client = self.selectedClient
      }
      if (self.selectedBuilder) {
        community.builder = self.selectedBuilder
      }

      self.$eventHub.$emit('show-modal-5', 'No Community Results Found', community, '500px')
    }
  }
}

export function autocompleteTextButNoValue(vue, ref, stateValue) {
  if (isAddOnTheFlyOrNoResultModalOpen(vue, ref)) {
    return
  }

  return vue.$refs[ref] && document.activeElement.name !== vue.$refs[ref]._props.name && vue.$refs[ref]._props.value && !stateValue
}

export function autocompleteTextButNoValueMessage(fieldName) {
  return `• No ${fieldName} Selected`
}

export function todayStart() {
  return moment().startOf('day').toDate()
}

export function todayEnd() {
  return moment().endOf('day').toDate()
}

export function endOfDay(date) {
  return moment(date).endOf('day').toDate()
}

export function nl2br(value) {
  return value != null ? value.replaceAll('\n', '<br />') : ''
}

export function assemblePlanNameDisplay(planName) {
  if (!planName) {
    return ''
  }

  let planNameDisplay = ''
  if (planName.name) {
    planNameDisplay += planName.name
  }
  if (planName.number) {
    if (planNameDisplay.length > 0) {
      planNameDisplay += ' '
    }
    planNameDisplay += planName.number
  }
  if (planName.hand) {
    if (planNameDisplay.length > 0) {
      planNameDisplay += ' - '
    }
    planNameDisplay += planName.hand
  }
  if (!planName.active && planNameDisplay.length > 0) {
    planNameDisplay += Constants.APPEND.INACTIVE
  }
  return planNameDisplay
}

export function messageForSafeDelete(things, prop, message) {
  message += '<ul>'
  things.forEach((thing) => {
    message += '<li>' + thing[prop] + '</li>'
  })
  message += '</ul>'
  return message
}

export function stringSort(a, b, prop) {
  if (!a || !a.hasOwnProperty(prop) || a[prop] === undefined || a[prop] == null || a[prop].length < 1) {
    return 1
  }
  if (!b || !b.hasOwnProperty(prop) || b[prop] === undefined || b[prop] == null || b[prop].length < 1) {
    return -1
  }
  const aString = a[prop].toUpperCase()
  const bString = b[prop].toUpperCase()
  return aString > bString ? 1 : aString < bString ? -1 : 0
}

export function maybeAppendInactive(data) {
  for (const entity of data) {
    if (entity.name) {
      entity.name = entity.active ? entity.name : entity.name + Constants.APPEND.INACTIVE
    }
  }

  return data
}

export function userDisplayNameLabel(results, findDisplayName, prop) {
  for (const result of results) {
    if (result.hasOwnProperty(prop)){
      result[prop + 'Label'] = findDisplayName(result[prop])
    }
  }
}

export function dateSort(a, b, prop) {
  if (!a || !a.hasOwnProperty(prop) || a[prop] === undefined) {
    return 1
  }
  if (!b || !b.hasOwnProperty(prop) || b[prop] === undefined) {
    return -1
  }
  return new Date(b[prop]) - new Date(a[prop])
}

export function numberInStringSort(a, b, prop) {
  if (!a || !a.hasOwnProperty(prop) || a[prop] === undefined || a[prop] == null) {
    return 1
  }
  if (!b || !b.hasOwnProperty(prop) || b[prop] === undefined || b[prop] == null) {
    return -1
  }
  const match1 = a[prop].match(/\d+/)
  const match2 = b[prop].match(/\d+/)

  if (match1 && match2) {
    const num1 = Number(match1[0])
    const num2 = Number(match2[0])
    if (num1 !== num2) {
      return num1 > num2 ? 1 : -1
    }
  }
  return a[prop].localeCompare(b[prop])
}

export function getTableHeight(headerBuffer) {
  return window.screen.height - headerBuffer
}

export function disableCache() {
  return '?_=' + new Date().getTime()
}

export function onUnload(event) {
  event.preventDefault();
  event.returnValue = ''
}

export function kvps(things, valueProp = 'id', labelProp = 'name') {
  const kvps = []
  if (Array.isArray(things)) {
    things.forEach((thing) => {
      kvps.push(
        {
          label: thing[labelProp],
          value: thing[valueProp]
        }
      )
    })
  }
  return kvps
}

export function kvpsActiveInactive(things, valueProp = 'id', labelProp = 'name') {
  let activeThings = _.filter(things, (thing) => thing.active)
  let inactiveThings = _.filter(things, (thing) => !thing.active)

  activeThings = sortByName(activeThings)
  inactiveThings = sortByName(inactiveThings)

  const allThings = activeThings.concat(inactiveThings)

  const kvps = []
  allThings.forEach((thing) => {
    const kvp = {
      label: _.get(thing, labelProp),
      value: _.get(thing, valueProp)
    }
    if (!thing.active) {
      kvp.label += ' - Inactive'
    }
    kvps.push(kvp)
  })

  return kvps
}

export function jobNamesForSelect(vue, departmentIds) {
  // if no department ID is provided, use the user's default department
  if (!departmentIds) {
    const currentUser = vue.$store.get('security/user')
    if (_.get(currentUser, 'preferences.global.defaultDepartment')) {
      departmentIds = [currentUser.preferences.global.defaultDepartment]
    }
  }

  if (departmentIds) {
    return vue.$store.get('camunda/processDefinitionsForSelectByDepartmentId', departmentIds)
  }

  return []
}

export async function findCompany(self, text, type) {
  let response = null
  if (autocompleteReady(text)) {
    const payload = {
      type: type,
      name: text
    }
    response = await self.$store.dispatch('companies/fetchByNameStartsWithAndType', payload)
    if (OK === response.status && response.data.length === 0) {
      if (type === Constants.COMPANY.TYPES.CLIENT) {
        self.newCompany.client = true
        self.newCompany.builder = false
        self.$refs.clientId.$refs.suggestions.showPopper = false
      } else if (type === Constants.COMPANY.TYPES.BUILDER) {
        self.newCompany.client = false
        self.newCompany.builder = true
        self.$refs.builderId.$refs.suggestions.showPopper = false
      }
      self.$eventHub.$emit('show-modal-4', 'No Company Results Found', payload, '500px')
    }
  }
  return response
}

export function isAddOnTheFlyOrNoResultModalOpen(vue, fieldName) {
  let addOnTheFlyOrNoResultModalOpen = false

  if (vue.activeModals) {
    for (const modal of vue.activeModals) {
      if ((fieldName.toLowerCase().includes('city') && modal.includes('city')) ||
          (fieldName.toLowerCase().includes('street') && modal.includes('street')) ||
          (fieldName.toLowerCase().includes('subdivision') && modal.includes('subdivision')) ||
          (fieldName.toLowerCase().includes('builder') && modal.includes('company')) ||
          (fieldName.toLowerCase().includes('client') && modal.includes('company')) ||
          (fieldName.toLowerCase().includes('community') && modal.includes('community')) ||
          (fieldName.toLowerCase().includes('plan') && modal.includes('plan-name'))
      ) {
        addOnTheFlyOrNoResultModalOpen = true
      }
    }

    return addOnTheFlyOrNoResultModalOpen
  }
}

export function verifyAutocompleteSelected(vue, vuexPath, fieldName, inputValue) {
  if (isAddOnTheFlyOrNoResultModalOpen(vue, fieldName)) {
    return
  }

  const message = `Please select a(n) ${fieldName} from the search results`
  setTimeout(() => {
    if (!vue.$store.get(vuexPath) && _.size(inputValue) > 0) {
      notifyWarning(vue, message)
    }
    vue.$forceUpdate()
  }, Constants.TIME.MILLIS.THIRD_SECOND)
}

export async function findCities(vue, text, cb, path) {
  if (autocompleteReady(text)) {
    const response = await vue.$store.dispatch('address/cityStartsWith', text)
    if (vue.$parent._props.title === 'Company Address') {
      vue.address.city = null
    }

    if (path) {
      vue.$store.set(path, null)
    }

    if (OK === response.status) {
      if (response.data.length > 0) {
        cb(response.data)
        return
      }

      vue.$refs.city.$refs.suggestions.showPopper = false // closes autocomplete results
      vue.$eventHub.$emit('show-modal-6', 'No City Results Found', text, '500px')
    }
  }
}

export function setCityLoadCountiesZips(vue, city, path) {
  vue.resetDependentAutocompleteFields()
  if (path) {
    vue.$store.set(path, city.name)
  }
  vue.localCity = city.name
  vue.counties = kvpsForSelectFromArray(city.counties)
  vue.zipCodes = kvpsForSelectFromArray(city.zipCodes)
}

export async function findStreets(vue, text, cb, path) {
  if (autocompleteReady(text, Constants.AUTOCOMPLETE.STREET_CHAR_LENGTH)) {
    vue.$store.set('address/streets', null)
    let city = null

    if (path) {
      vue.$store.set(path + 'streetName', null)
      city = vue.$store.get(path + 'city')
    }

    if (vue.$parent._props.title === 'Company Address') {
      vue.address.streetName = null
      city = vue.address.city
    }

    if (city) {
      const payload = {
        city: city,
        streetName: text
      }
      await vue.$store.dispatch('address/streetStartsWithAndCityEquals', payload)
      const streets = vue.$store.get('address/streets')

      if (streets && streets.length > 0) {
        cb(streets)
        return
      }

      if (vue.localStreetName) {
        let street = vue.localStreetName
        const streetParts = street.split(' ')
        const numSpaces = streetParts.length - 1

        // Strip off last part, if there are multiple
        if (numSpaces === 1) {
          street = streetParts[0] // EX: Pecos Str -> Pecos
        } else if (numSpaces > 1) {
          street = `${streetParts[0]} ${streetParts[1]}` // EX: N. Pecos Str -> N. Pecos
        }

        const streetPayload = {
          name: street,
          cities: [city],
          active: true
        }

        vue.$refs.streetName.$refs.suggestions.showPopper = false // closes autocomplete results
        vue.$eventHub.$emit('show-modal-6', 'Street Results', streetPayload, '750px')
      }
    }
  }
}

export async function saveStreet(vue) {
  const response = await vue.$store.dispatch('save', vue.$store.get('streets/savePayload', 'streets'))

  if (response.result === Constants.RETURN_CODES.SUCCESS) {
    vue.$store.commit('streets/resetEdit')
    vue.$eventHub.$emit('streetSaved', response.data)
    vue.streetSaved = true
    vue.$emit('close')
    notifySuccess(vue, 'Street Saved Successfully')
  } else if (response.result === Constants.RETURN_CODES.ERROR) {
    notifyError(vue, 'Error Saving Street', response.errors)
  }
}

export async function findLegalSubdivisions(vue, text, cb, path) {
  if (autocompleteReady(text)) {
    vue.$store.set(path + 'legalSubdivisionId', null)

    await vue.$store.dispatch('legalSubdivisions/fetchByNameStartsWith', text)
    const legalSubdivisions = vue.$store.get('legalSubdivisions/autocompleteResults')

    if (legalSubdivisions && legalSubdivisions.length > 0) {
      cb(legalSubdivisions)
      return
    }

    const legalSubdivision = {
      id: new ObjectID(),
      name: text,
      active: true
    }
    vue.$refs.legalSubdivisionId.$refs.suggestions.showPopper = false // closes autocomplete results
    vue.$eventHub.$emit('show-modal-3', 'No Legal Subdivision Results Found', legalSubdivision, '500px')
  }
}

export function verifyAutocompleteSelectedAndListPopulated(vue, vuexPath, fieldName, searchInput, arrayProperty) {
  setTimeout(() => {
    const entity = vue.$store.get(vuexPath)
    if (vue[searchInput] && vue[searchInput].length > 0 && entity[arrayProperty].length === 0) {
      const message = `Please select a ${fieldName} from the search results.`
      notifyWarning(vue, message)
    }
  }, Constants.TIME.MILLIS.THIRD_SECOND)
}

export function maxResultsWarning(vue, responseCount) {
  const message = `Maximum search results is ${responseCount}. Refine search criteria and try again.`
  setTimeout(() => {
    if (Number.isInteger(responseCount)) {
      notifyWarning(vue, message)
    }
  }, Constants.TIME.MILLIS.THIRD_SECOND)
}

export async function sleepOneSecond() {
  return new Promise(res => setTimeout(res, Constants.TIME.MILLIS.ONE_SECOND))
}

export async function sleepSeconds(seconds) {
  return new Promise(res => setTimeout(res, seconds * Constants.TIME.MILLIS.ONE_SECOND))
}

export function setIndexColumnWidth(totalResults) {
  return totalResults >= 100 ? Constants.INDEX_COLUMN_WIDTHS.WIDE : Constants.INDEX_COLUMN_WIDTHS.NARROW
}

export function noteIconSlice(department) {
  return department.slice(0, -2)
}

export function emailUsername(email) {
  if (email && email.includes('@')) {
    return email.substring(0, email.indexOf('@')).toLowerCase()
  }
  return email
}

export function defaultUserPreferences() {
  const defaultPreferences = {}

  for (let preferenceTypeProp in Constants.USER_PREFERENCE_TYPES) {
    defaultPreferences[Constants.USER_PREFERENCE_TYPES[preferenceTypeProp]] = {}
  }

  return defaultPreferences
}

export function defaultAdminAppPreferences() {
  const defaultPreferences = {}

  for (let preferenceTypeProp in Constants.ADVANCED_USER_PREFERENCE_TYPES) {
    defaultPreferences[Constants.USER_PREFERENCE_TYPES[preferenceTypeProp]] = []
  }

  return defaultPreferences
}

export function daysOffMergePatchPayload(user) {
  return {
    id: user.id || user.claims.sub,
    endpoint: Constants.ENTITY_ENDPOINTS.USERS,
    data: {
      daysOff: user.daysOff
    }
  }
}

export function userPreferencesMergePatchPayload(user) {
  console.log('userPreferencesMergePatchPayload', user)

  const patchPayload = {
    id: user.id || parseActiveDirectoryUserId(user.claims.sub),
    endpoint: Constants.ENTITY_ENDPOINTS.USERS,
    data: {
      preferences: JSON.parse(JSON.stringify(user.preferences, (key, value) => {
        if (typeof value === 'boolean') {
          return value.toString()
        }
        return value
      }))
    }
  }

  if (user.hasOwnProperty('username')) {
    patchPayload.data.username = user.username
  }

  if (user.hasOwnProperty('name')) {
    patchPayload.data.name = user.name
  }

  if (user.hasOwnProperty('email')) {
    patchPayload.data.email = user.email
  }

  return patchPayload
}

export function userAdminAppPreferencesMergePatchPayload(user) {
  return {
    id: user.id || user.claims.sub,
    endpoint: Constants.ENTITY_ENDPOINTS.USERS,
    data: {
      adminAppPreferences: user.adminAppPreferences
    }
  }
}

export function assembleSortColumnsFromSelectedAdminAppPreferences(selectedAdminAppPreferences) {
  const sortColumns = []
  for (const selectedPreference of selectedAdminAppPreferences) {
    sortColumns.push({
      name: selectedPreference.name,
      sortDirection: selectedPreference.sortDirection || null,
      sortOrder: selectedPreference.sortOrder
    })
  }
  return sortColumns
}

export function notSelectedAdminAppPreferences(allPreferences, selectedPreferences) {
  const allPreferencesSorted = sortBy(allPreferences, 'name')
  const allPreferencesNotAlreadySelected = []
  for (const allPref of allPreferencesSorted) {
    if (!selectedPreferences.some(e => e.name === allPref.name)) {
      allPreferencesNotAlreadySelected.push(allPref)
    }
  }

  for (let i = 0; i < allPreferencesNotAlreadySelected.length; i++) {
    allPreferencesNotAlreadySelected[i].sortOrder = i + 1
  }

  return allPreferencesNotAlreadySelected
}

export function userMergePutPayload(user) {
  return {
    id: user.id || user.claims.sub,
    endpoint: Constants.ENTITY_ENDPOINTS.USERS,
    data: JSON.parse(JSON.stringify(user))
  }
}

// see: https://gist.github.com/djD-REK/068cba3d430cf7abfddfd32a5d7903c3 for details
export function roundAccurately(number, decimalPlaces) {
  return Number(Math.round(number + 'e' + decimalPlaces) + 'e-' + decimalPlaces)
}

export function getParentEntityIdProjectHash(projectId) {
  return hash({
    projectId
  })
}

export function getParentEntityIdProjectDepartmentHash(projectId, departmentId) {
  return hash({
    projectId,
    departmentId
  })
}

export function getParentEntityIdProjectDepartmentJobHash(projectId, departmentId, jobId) {
  return hash({
    projectId,
    departmentId,
    jobId
  })
}

export function sortableTable(querySelector, sortFunction) {
  const table = document.querySelector(querySelector)

  if (table) {
    Sortable.create(table, {
      dataIdAttr: 'name',
      onEnd() {
        let sort = 1
        const sortedFieldBlocks = {}
        for (let i = 0; i < table.rows.length; i++) {
          const $ = cheerio.load(table.rows[i].cells[0].innerHTML)
          const property = $('div').text().trim()
          sortedFieldBlocks[property] = sort
          sort++
        }
        sortFunction(sortedFieldBlocks)
      }
    })
  }
}

export function saveFileToBrowserDownloads(data, fileName, openFile) {
  const blob = new Blob([data], {type: mime.lookup(fileName)})
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  if (openFile) {
    link.target = '_blank'
  } else {
    link.download = fileName
  }
  link.click()
}

export function formatTaskFieldKey(taskTemplates, task, fieldKey) {
  const taskTemplate = objectById(taskTemplates, task.templateId)
  if (taskTemplate) {
    const field = objectBy(taskTemplate.fields, 'key', fieldKey)
    if (field) {
      return field.label
    }
  }
  // fallback is the above fails for some reason
  return capitalCase(fieldKey)
}

export function isEmptyString(val) {
  return val !== null && !val.trim()
}

export function filterAndSortTableColumns(tableColumns, user, preferenceType) {
  const sortColumns = user.adminAppPreferences[preferenceType]
  if (!Array.isArray(sortColumns)) {
    return []
  }

  const sortColumnsMap = {}
  for (const sortColumn of sortColumns) {
    sortColumnsMap[sortColumn.name] = sortColumn
  }
  const columns = sortColumns.map(sortColumn => sortColumn.name)
  const filteredColumns = tableColumns.filter(tableColumn => columns.includes(tableColumn.label))
  for (const filteredColumn of filteredColumns) {
    const sortColumn = sortColumnsMap[filteredColumn.label]
    filteredColumn.sortDirection = sortColumn.sortDirection
    filteredColumn.sortOrder = sortColumn.sortOrder
  }
  return sortBy(filteredColumns, 'sortOrder')
}

export function assembleTableColumnSortProps(filteredTableColumns) {
  if (!Array.isArray(filteredTableColumns)) {
    return []
  }
  const props = []
  for (const column of filteredTableColumns) {
    if (column.sortDirection) {
      let prop = column.sortProp
      if (column.sortDirection === 'descending') {
        prop = '-' + prop
      }
      props.push(prop)
    }
  }
  return props
}

export function tableHeaderSortCssClass(filteredColumns, columnIndex, firstColumnOffset) {
  if (!filteredColumns) {
    return null
  }

  if (filteredColumns[columnIndex - firstColumnOffset] && filteredColumns[columnIndex - firstColumnOffset].sortDirection) {
    return filteredColumns[columnIndex - firstColumnOffset].sortDirection === 'ascending' ? 'sort-asc' : 'sort-desc'
  }

  return null
}

// Street Number, Street Name, Building, Floor, Suite, Lot, Block, City, State
export function assembleAddressForTable(address) {
  return (address.streetNumber && address.streetName ? address.streetNumber + ' ' : '') +
      (address.streetName ? address.streetName + ', ' : '') +
      (address.buildingNumber ? 'Building: ' + address.buildingNumber + ', ' : '') +
      (address.floorNumber ? 'Floor: ' + address.floorNumber + ', ' : '') +
      (address.suiteNumber ? 'Suite: ' + address.suiteNumber + ', ' : '') +
      (address.lotNumber ? 'Lot: ' + address.lotNumber + ', ' : '') +
      (address.blockNumber ? 'Block: ' + address.blockNumber + ', ' : '') +
      (address.city ? address.city + ', ' : '') + address.state
}

export const sortNested = (props) => (a, b) => {
  for (const prop of props) {
    if (!prop) {
      return
    }

    const ascValue = prop.startsWith('-') ? -1 : 1
    const cleanProp = prop.startsWith('-') ? prop.substring(1) : prop
    const aa = lodash.get(a, cleanProp)
    const bb = lodash.get(b, cleanProp)
    const nestedProp = cleanProp.includes('.') ? cleanProp.substring(cleanProp.lastIndexOf('.') + 1) : cleanProp

    if (aa) {
      if (bb) {
        if (aa !== bb) {
          if (Constants.SORT_PROPS.NUMBER_IN_STRING.includes(nestedProp)) {
            const match1 = aa.match(/\d+/)
            const match2 = bb.match(/\d+/)

            if (match1 && match2) {
              const num1 = Number(match1[0])
              const num2 = Number(match2[0])
              if (num1 !== num2) {
                return num1 > num2 ? ascValue : -ascValue
              }
            }

            return aa.localeCompare(bb)
          }

          if (Constants.SORT_PROPS.NUMBER.some(term => prop.includes(term))) {
            return parseFloat(aa) > parseFloat(bb) ? ascValue : -ascValue
          }

          if (prop.toLowerCase().includes(Constants.SORT_PROPS.DATE)) {
            return new Date(aa) > new Date(bb) ? ascValue : -ascValue
          }

          if (prop.includes(Constants.SORT_PROPS.FIELD_VALUES)) {
            return JSON.stringify(aa) > JSON.stringify(bb) ? ascValue : -ascValue
          }

          if (prop === Constants.SORT_PROPS.ADDRESS) {
            const aaAddress = assembleAddressForTable(aa)
            const bbAddress = assembleAddressForTable(bb)
            return aaAddress.toUpperCase() > bbAddress.toUpperCase() ? ascValue : -ascValue
          }

          if (typeof aa === 'string' && typeof bb === 'string') {
            return aa.toUpperCase() > bb.toUpperCase() ? ascValue : -ascValue
          }

          return aa > bb ? ascValue : -ascValue
        }
      } else {
        return ascValue
      }
    } else if (bb) {
      return -ascValue
    }
  }

  return 0
}

export function objectFieldsToCsv(object) {
  if (!object) {
    return null
  }

  let output = ''

  const props = Object.keys(object)

  for (let i = 0; i < props.length; i++) {
    const prop = props[i]
    if (object[prop] != null) {
      output += capitalCase(prop) + ': ' + object[prop]
      if (i < props.length - 1) {
        output += ', '
      }
    }
  }

  return output
}

export function isValidDate(dateObject) {
  return new Date(dateObject).toString() !== 'Invalid Date';
}
